import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { makeStyles, createStyles } from "@mui/styles";
import {
  Button,
  CircularProgress,
  debounce,
  Paper,
  SxProps,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import theme from "../../../components/theme/theme";
import { Link, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../common/store/store";
import { getUserInputForStepId } from "../../../components/wizard/util";
import {
  Alternatives,
  NumericParameterStyle,
  Parameter,
  ParameterType,
  ParameterValue,
} from "../../../common/types/parameters";
import {
  calculateOutput,
  getModules,
  wasCalculationAborted,
} from "../../../api/module-api";
import {
  getParameterStates,
  setUserInput as setModuleUserInput,
  resetUserInputsWithId as resetModuleUserInputsWithId,
} from "../../../common/store/module-parameters-slice";
import {
  format,
  getDisplayValue,
  getType,
  parseAsSelectionAlternative,
} from "../../../components/data-presentation/util";
import useAPIError from "../../../common/hooks/useAPIError";
import { isCalculated } from "../../../common/util/heating-technology-visibility";
import { getLabel } from "../../../api/label-api";
import ParameterInput from "../../../components/parameters/parameter-input";
import CostPresentation, { Representation } from "./cost-presentation";
import useWizard from "../../../common/hooks/useWizard";
import useHeatingTechnologySelection from "../../../common/hooks/useHeatingTechnologySelection";
import { isDistrictHeatingSelectedAsSupplementalHeatingSource } from "../supplemental-heating.util";

const useStyles = makeStyles(() =>
  createStyles({
    card: {
      padding: "15px",
    },
  })
);

const calcModuleId = "detaljer";

export default function OversiktligaResultat(params: {
  forceParameterReload?: boolean;
  onReload?: () => void;
  sx?: SxProps;
}) {
  let { customer } = useParams();
  let { economicProfile } = useParams();
  const { addError, deleteError } = useAPIError();
  const {
    steps: wizardSteps,
    updateParameterValue: updateWizardParameterValue,
    isWizardParameter,
    wizardIsCompleted,
  } = useWizard(customer!, economicProfile!);

  const [detailsModuleIsAvailable, setDetailsModuleIsAvailble] =
    useState(false);

  useEffect(() => {
    if (customer !== undefined && economicProfile !== undefined) {
      getModules(customer, economicProfile).then((availableModules) => {
        setDetailsModuleIsAvailble(
          availableModules.findIndex((module) => module.id === calcModuleId) >=
            0
        );
      });
    }
  }, [customer, economicProfile]);

  let moduleParameterInputs = useAppSelector((state) =>
    getParameterStates(state, {
      customer: customer,
      economicProfile: economicProfile,
    })
  );
  const [outputParameters, setOutputParameters] = useState<Parameter[]>([]);
  const includeVat: boolean = JSON.parse(
    outputParameters.find((parameter) => parameter.id === "VATSelection")
      ?.value ?? "false"
  );

  const [districtHeatingLabels, setDistrictHeatingLabels] =
    useState<Parameter>();
  const [groundSourceHeatPumpLabels, setGroundSourceHeatPumpLabels] =
    useState<Parameter>();
  const [airSourceHeatPumpLabels, setAirSourceHeatPumpLabels] =
    useState<Parameter>();
  const [exhaustAirHeatPumpLabels, setExhaustAirHeatPumpLabels] =
    useState<Parameter>();
  const [pelletLabels, setPelletLabels] = useState<Parameter>();

  const { isHidden } = useHeatingTechnologySelection();

  const heatingTechnologies = [
    {
      id: "DistrictHeating",
      title: districtHeatingLabels?.displayName || "Fjärrvärme",
    },
    {
      id: "GroundSource",
      title: groundSourceHeatPumpLabels?.displayName || "Bergvärme",
    },
    {
      id: "AirSource",
      title: airSourceHeatPumpLabels?.displayName || "Luft/vatten-värmepump",
    },
    {
      id: "ExhaustAir",
      title: exhaustAirHeatPumpLabels?.displayName || "Frånluftsvärmepump",
    },
    {
      id: "Pellet",
      title: pelletLabels?.displayName || "Pellets",
    },
  ].filter((tech) => isCalculated(tech, outputParameters));

  const dispatch = useAppDispatch();

  function onValidParameterChange(id: string, value: string) {
    if (isWizardParameter(id)) {
      let parameter = outputParameters.find((parameter) => parameter.id === id);

      if (parameter !== null && parameter !== undefined) {
        saveWizardParameter(
          id,
          value,
          getDisplayValue(
            value,
            includeVat,
            getType(parameter),
            parameter?.stylingConfig,
            true
          ) ?? value
        );
      }
    } else {
      saveModuleParameter(id, value);
    }
  }

  function saveWizardParameter(
    id: string,
    value: string,
    formattedValue: string
  ) {
    updateWizardParameterValue({ id, value }, formattedValue);
  }

  function saveModuleParameter(id: string, value: string) {
    dispatch(
      setModuleUserInput({
        id: id,
        value: value,
        profile: { customer: customer, economicProfile: economicProfile },
      })
    );
  }

  const calculateOutputDelayed = useCallback(
    debounce(
      (inputs: ParameterValue[], callback: (output: Parameter[]) => void) => {
        calculateOutput(calcModuleId, inputs, customer, economicProfile)
          .then((calculationResult) => {
            if (wasCalculationAborted(calculationResult)) {
              return;
            }

            callback(calculationResult);
          })
          .catch(() => addError("Error"));
      },
      600
    ),
    [customer, economicProfile]
  );

  useEffect(() => {
    if (wizardIsCompleted) {
      let inputs = wizardSteps
        .flatMap((step) => step.inputParameters)
        .map(
          (parameter) =>
            ({ id: parameter.id, value: parameter.value }) as ParameterValue
        )
        .concat(moduleParameterInputs);
      calculateOutputDelayed(inputs, (parameters) => {
        deleteError();
        setOutputParameters(
          mergeSupplementalDistrictHeatingIntoBaseCosts(parameters)
        );
      });
    }
  }, [
    moduleParameterInputs,
    customer,
    economicProfile,
    addError,
    deleteError,
    wizardIsCompleted,
    wizardSteps,
    calculateOutputDelayed,
  ]);

  useEffect(() => {
    if (params.forceParameterReload) {
      // Check if parameters should be reloaded
      let paramsToReset = outputParameters
        .filter((parameter) =>
          isSelectionParameterWithUnmodifiableAlternatives(parameter)
        )
        .map((parameter) => parameter.id);

      dispatch(
        resetModuleUserInputsWithId({
          ids: paramsToReset,
          profile: { customer: customer, economicProfile: economicProfile },
        })
      );

      if (params.onReload !== undefined && params.onReload !== null) {
        params.onReload();
      }
    }
  }, [customer, dispatch, economicProfile, outputParameters, params]);

  useEffect(() => {
    let districtValue = getUserInputForStepId(
      "DistrictSelected",
      wizardSteps
    )?.at(0)?.value;
    let buildingValue = getUserInputForStepId(
      "BuildingProfile",
      wizardSteps
    )?.at(0)?.value;
    const district = parseAsSelectionAlternative(districtValue)?.value;
    const building = parseAsSelectionAlternative(buildingValue)?.value;
    if (district !== undefined && building !== undefined) {
      getLabel(
        "DistrictHeatingTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setDistrictHeatingLabels(response);
      });
      getLabel(
        "GroundSourceHeatPumpTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setGroundSourceHeatPumpLabels(response);
      });
      getLabel(
        "AirSourceHeatPumpTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setAirSourceHeatPumpLabels(response);
      });
      getLabel(
        "ExhaustAirHeatPumpTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setExhaustAirHeatPumpLabels(response);
      });
      getLabel("PelletTab", customer, district, building, economicProfile).then(
        (response) => {
          setPelletLabels(response);
        }
      );
    }
  }, [customer, economicProfile, wizardSteps]);

  const classes = useStyles(theme);

  let interestRateParam = outputParameters.find(
    (param) => param.id === "InterestRate"
  );
  return outputParameters.length > 0 ? (
    <Box key="oversikt-module-container" sx={params.sx}>
      <Typography variant="h1">Översikt</Typography>
      <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
        <Box gridColumn="span 12">
          <Paper className={classes.card} elevation={3}>
            <Typography paragraph>
              Nedan ges en översiktlig sammanställning av värmekostnader utifrån
              det du angivit i guiden. Värmebehovet du valt kan justeras nedan.
              Om du vill ändra andra val i guiden kan du gå tillbaka via
              genvägarna till vänster. Om du vill se mer detaljerade resultat
              och justera ekonomiska och tekniska parametrar kan detta göras i
              ”Detaljerad kostnadskalkyl” som du når via knappen nedan eller via
              genvägarna till vänster.
            </Typography>
            <Typography paragraph>
              {`Samtliga kostnader är ${
                includeVat ? "inklusive" : "exklusive"
              } moms. Kapitalkostnaden baseras på ${format(
                interestRateParam!.value,
                includeVat,
                interestRateParam!.stylingConfig ?? undefined
              )}${
                (interestRateParam!.stylingConfig as NumericParameterStyle)
                  ?.unit
              } ränta.`}
            </Typography>
            <ParameterInput
              parameter={
                outputParameters.find(
                  (param) => param.id === "AnnualHeatingDemand"
                )!
              }
              disabled={false}
              onValidChange={onValidParameterChange}
              includeVat={includeVat}
            />
          </Paper>
        </Box>
        <Box gridColumn="span 7">
          <CostPresentation
            heatingTechnologies={heatingTechnologies.map(
              (heatingTechnology) => ({
                ...heatingTechnology,
                hidden: isHidden(heatingTechnology.id),
              })
            )}
            parameters={outputParameters}
            includeVat={includeVat}
            type={Representation.Table}
          />
        </Box>
        <Box gridColumn="span 5">
          <Paper elevation={3}>
            <CostPresentation
              heatingTechnologies={heatingTechnologies.map(
                (heatingTechnology) => ({
                  ...heatingTechnology,
                  hidden: isHidden(heatingTechnology.id),
                })
              )}
              parameters={outputParameters}
              includeVat={includeVat}
              type={Representation.Chart}
            />
          </Paper>
        </Box>
        {detailsModuleIsAvailable && (
          <Box gridColumn="span 12" sx={{ textAlign: "center" }}>
            <Button
              component={Link}
              to={`/${customer}/${economicProfile}/detaljer`}
              color="button"
              variant="contained"
              sx={{ marginTop: 3, marginBottom: 12 }}
            >
              Se detaljerad kostnadskalkyl
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  ) : (
    <Box sx={{ textAlign: "center", ...params.sx }}>
      <CircularProgress />
    </Box>
  );
}

function isSelectionParameterWithUnmodifiableAlternatives(
  parameter: Parameter
): boolean {
  return (
    getType(parameter) === ParameterType.Selection &&
    (parameter.constraints as Alternatives) &&
    (parameter.constraints as Alternatives)
      .flatMap((alternativeList) => alternativeList)
      .some((alternative) => !alternative.editable)
  );
}

const HEATING_SOURCES = ["AirSource", "GroundSource", "ExhaustAir"] as const;
const COST_TYPES = ["CapitalCost", "RunningCostsSum"] as const;
const SUPPLEMENTAL_INFIX = "SupplementalDistrictHeating";

function mergeSupplementalDistrictHeatingIntoBaseCosts(
  parameters: Parameter[]
): Parameter[] {
  if (!isDistrictHeatingSelectedAsSupplementalHeatingSource(parameters)) {
    return parameters;
  }

  return parameters.map((parameter) => {
    for (const source of HEATING_SOURCES) {
      for (const costType of COST_TYPES) {
        const baseId = `${source}${costType}`;
        if (parameter.id !== baseId) continue;

        const supplementalId = `${source}${SUPPLEMENTAL_INFIX}${costType}`;
        const supplemental = Number(
          parameters.find((item) => item.id === supplementalId)?.value ?? 0
        );
        return {
          ...parameter,
          value: (Number(parameter.value ?? 0) + supplemental).toString(),
        } as Parameter;
      }
    }
    return parameter;
  });
}
